import { FC, useState, useMemo, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import type { ObjectSchema, Schema } from 'yup';

import Form, { FormInput, FormSelect, FormPhoneFiled } from 'components/common/Form';
import useAuth, { AuthRegisterSchemaType } from 'store/auth';
import useAllCurrencies from 'store/currencies';
import useConfiguration from 'store/configuration';
import { createYupSchema, registerSchema } from 'configs/validationSchemes';
import useYupValidationResolver from 'hooks/useYupValidationResolver';
import useToaster from 'hooks/useToaster';
import { TranslationKey } from 'utils/translate';
import { CustomFieldType } from 'types';
import Modal from 'components/common/Modal';
import DotIcon from 'components/common/icons/Dot';
import useScreen from 'hooks/useScreen';
import useQueryParams from 'hooks/useQueryParams';

import sx from './styles';

export interface ISignUpFormModalProps {
  title: TranslationKey;
  onClose: () => void;
}

const SignUpFormModal: FC<ISignUpFormModalProps> = ({ title, onClose }) => {
  const [loading, setLoading] = useState(false);
  const notify = useToaster();
  const queryParams = useQueryParams();
  const [authState, authActions] = useAuth();
  const [currenciesState] = useAllCurrencies();
  const [configurationState] = useConfiguration();
  const { isMobile } = useScreen();
  const refQuery = queryParams.get('ref');

  const validationSchema = useMemo(() => {
    return createYupSchema(
      (configurationState.fields ?? []) as CustomFieldType[],
      registerSchema as unknown as ObjectSchema<Schema>,
    );
  }, [configurationState.fields]);

  const resolver = useYupValidationResolver<AuthRegisterSchemaType>(
    validationSchema as ObjectSchema<AuthRegisterSchemaType>,
  );
  const { control, handleSubmit, setValue } = useForm<AuthRegisterSchemaType>({
    resolver,
  });

  const currencies = useMemo(() => {
    if (currenciesState.publicCurrencies?.length > 0) {
      return currenciesState.publicCurrencies?.map((i) => {
        return {
          id: i.code,
          name: i.name,
        };
      });
    }
    return [];
  }, [currenciesState?.publicCurrencies]);

  useEffect(() => {
    if (currencies.length > 0 && currencies[0]) {
      setValue('currencyCode', currencies[0].id);
    }
  }, [currencies, setValue]);

  const handleSignUpSubmit = async (data: AuthRegisterSchemaType): Promise<void> => {
    setLoading(true);
    try {
      const requestObj = {
        ...data,
        ref: refQuery,
      };

      const customFieldObj: FieldValues = {};
      (configurationState.fields ?? []).forEach((i) => {
        const key = i.key as keyof FieldValues;
        customFieldObj[key] = (data as FieldValues)?.[key];
      });

      requestObj.meta = configurationState.fields?.length > 0 ? JSON.stringify(customFieldObj) : null;

      const response = await authActions.signUp(requestObj);
      if (response.status === 200) {
        onClose();
        notify({
          deleteDelay: 20000,
          message: 'Verification link has been sent to your email please check it',
          type: 'success',
        });
      }
      if (response.code === 400) {
        notify({
          deleteDelay: 10000,
          message: response.text as TranslationKey,
          type: 'error',
        });
      }
    } catch (error) {
      notify({
        message: ((error as Error)?.message || 'Something went wrong') as TranslationKey,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (): Promise<void> => {
    handleSubmit(handleSignUpSubmit)();
  };

  return (
    <Modal
      title={title}
      fullScreen={isMobile}
      icon={<DotIcon fill="red.scale.300" />}
      saveText="Sign up"
      isLoading={loading || authState?.isLoading}
      onSave={handleSubmit(onSave)}
      onClose={onClose}
      maxWidth={isMobile ? 600 : 400}
    >
      <Form sx={sx.form} spacing={2.5} onSubmit={handleSubmit(handleSignUpSubmit)}>
        <FormInput<AuthRegisterSchemaType> required label="Username" control={control} name="username" />
        <FormInput<AuthRegisterSchemaType> required label="Email" control={control} name="email" />
        <FormPhoneFiled<AuthRegisterSchemaType> label="Phone" control={control} name="phoneNumber" />
        <FormSelect<AuthRegisterSchemaType>
          required
          label="Currency"
          name="currencyCode"
          control={control}
          options={currencies}
        />
        {configurationState.fields.map((field) => (
          <FormInput<AuthRegisterSchemaType>
            key={field.key}
            name={field.key}
            required={field.isRequired}
            label={field.hint as TranslationKey}
            control={control}
          />
        ))}
        <FormInput<AuthRegisterSchemaType>
          required
          label="Password"
          control={control}
          name="password"
          type="password"
        />
        <FormInput<AuthRegisterSchemaType>
          required
          label="Confirm password"
          control={control}
          name="confirmPassword"
          type="password"
        />
      </Form>
    </Modal>
  );
};

export default SignUpFormModal;
